import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiGet, apiPost } from "../../services/api";
import { useToast } from "../common/ToastProvider";
import Button from "../common/Button";
export interface OnlineAppointmentRow {
  visit_id: number;
  patient_id: string;
  patient_name?: string;
  visit_date?: string;
  appointment_time?: string | null;
  assigned_doctor_id?: number | null;
  doctor_name?: string | null;
  status?: string;
  remarks?: string;
  is_locked?: boolean;
  created_at?: string;
  [key: string]: unknown;
}

interface DoctorRow {
  id: number;
  username?: string;
  full_name?: string;
  [key: string]: unknown;
}

function toISODate(d: Date): string {
  const x = new Date(d);
  x.setMinutes(x.getMinutes() - x.getTimezoneOffset());
  return x.toISOString().slice(0, 10);
}

function isoToHHMM(value: unknown): string {
  const s = String(value ?? "");
  if (!s) return "";
  // Support "YYYY-MM-DD HH:MM:SS" or ISO "YYYY-MM-DDTHH:MM:SS"
  const t = s.includes("T") ? s.split("T")[1] : s.includes(" ") ? s.split(" ")[1] : "";
  if (!t) return "";
  return t.slice(0, 5);
}

export default function OnlineAppointmentsPanel({
  open,
  onClose,
  onChanged,
}: {
  open: boolean;
  onClose: () => void;
  onChanged?: () => void;
}) {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<OnlineAppointmentRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [dateFrom, setDateFrom] = useState(() => toISODate(new Date()));
  const [dateTo, setDateTo] = useState(() => toISODate(new Date(Date.now() + 14 * 86400000)));
  const [status, setStatus] = useState<string>("all");

  const [doctors, setDoctors] = useState<DoctorRow[]>([]);
  const [edits, setEdits] = useState<Record<number, { appointment_time?: string; assigned_doctor_id?: number | null }>>({});

  const loadDoctors = useCallback(async () => {
    if (!open) return;
    try {
      const data = (await apiGet("/api/reception/doctors")) as DoctorRow[];
      setDoctors(Array.isArray(data) ? data : []);
    } catch {
      setDoctors([]);
    }
  }, [open]);

  const load = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      qs.set("date_from", dateFrom);
      qs.set("date_to", dateTo);
      if (status) qs.set("status", status);
      const data = (await apiGet(`/api/reception/online-appointments?${qs.toString()}`)) as OnlineAppointmentRow[];
      const list = Array.isArray(data) ? data : [];
      setRows(list);
      setEdits((prev) => {
        const next = { ...prev };
        for (const r of list) {
          if (!r?.visit_id) continue;
          if (!next[r.visit_id]) {
            next[r.visit_id] = {
              appointment_time: isoToHHMM(r.appointment_time),
              assigned_doctor_id: typeof r.assigned_doctor_id === "number" ? r.assigned_doctor_id : null,
            };
          }
        }
        return next;
      });
    } catch (e: unknown) {
      setRows([]);
      setError((e as Error)?.message || "Failed to load online appointments");
    } finally {
      setLoading(false);
    }
  }, [open, dateFrom, dateTo, status]);

  useEffect(() => {
    void load();
    void loadDoctors();
  }, [load, loadDoctors]);

  const pendingCount = useMemo(
    () => rows.filter((r) => ["BOOKED", "CONFIRMED", "RESCHEDULED"].includes(String(r.status || "").toUpperCase())).length,
    [rows]
  );

  const act = async (visitId: number, action: "confirm" | "cancel" | "check-in" | "reschedule", payload?: unknown) => {
    setLoading(true);
    setError(null);
    try {
      await apiPost(`/api/reception/online-appointments/${visitId}/${action}`, payload ?? {});
      showToast({ message: `Appointment ${action}ed successfully.`, type: "success" });
      onChanged?.();
      await load();
    } catch (e: unknown) {
      showToast({ message: `Failed to ${action} appointment.`, type: "error" });
      setError((e as Error)?.message || "Action failed");
    } finally {
      setLoading(false);
    }
  };

  const handleReschedule = async (visitId: number) => {
    const d = window.prompt("Reschedule to date (YYYY-MM-DD):");
    if (!d) return;
    const edit = edits[visitId] || {};
    await act(visitId, "reschedule", {
      visit_date: d,
      appointment_time: edit.appointment_time || null,
      assigned_doctor_id: edit.assigned_doctor_id ?? null,
    });
  };

  if (!open) return null;

  return (
    <div className="rc-online-overlay" onClick={onClose}>
      
      <aside className="rc-online-panel" onClick={e => e.stopPropagation()}>
        
        <div className="rc-online-header">
          <div>
            <h3>🌐 Online Appointments</h3>
            <p className="rc-online-sub">Manage web bookings & schedules</p>
          </div>
          <button onClick={onClose} className="rc-online-close">✕ Close</button>
        </div>

        <div className="rc-online-filters">
          <div className="rc-online-field">
            <label>Date from</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div className="rc-online-field">
            <label>Date to</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
          <div className="rc-online-field">
            <label>Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="all">All</option>
              <option value="BOOKED">Booked</option>
              <option value="CONFIRMED">Confirmed</option>
              <option value="RESCHEDULED">Rescheduled</option>
              <option value="CANCELLED">Cancelled</option>
              <option value="CHECKED_IN">Checked-in</option>
              <option value="IN_CHAIR">In-chair</option>
              <option value="LOCKED">Locked</option>
            </select>
          </div>
          <Button variant="outline" onClick={() => void load()} isLoading={loading}>
            Refresh
          </Button>
        </div>

        {error && <div className="rc-online-error">{error}</div>}

        <div className="rc-online-body">
          {loading && !rows.length ? (
             <div className="rc-online-loading">Loading appointments...</div>
          ) : rows.length === 0 ? (
             <div className="mod-empty-state rc-online-empty">No appointments match your filters.</div>
          ) : (
            rows.map((r) => (
              <div key={r.visit_id} className="rc-online-card">
                 <div className="rc-online-card-header">
                   <div>
                     <div className="rc-online-patient">{r.patient_name || "Unknown"}</div>
                     <div className="rc-online-meta">
                        <span className="rc-online-id">#{r.patient_id}</span>
                        <span>•</span>
                        <span>{r.visit_date}</span>
                        {r.appointment_time && (
                          <>
                            <span>•</span>
                            <span>🕒 {isoToHHMM(r.appointment_time) || "—"}</span>
                          </>
                        )}
                        {r.doctor_name && (
                           <>
                             <span>•</span>
                             <span className="rc-online-doctor">🧑‍⚕️ {r.doctor_name}</span>
                           </>
                        )}
                     </div>
                   </div>
                   <div
                     className={`rc-online-status ${
                       r.status === 'BOOKED'
                         ? 'booked'
                         : r.status === 'CONFIRMED'
                           ? 'confirmed'
                           : r.status === 'CHECKED_IN'
                             ? 'checked'
                             : 'neutral'
                     }`}
                   >
                     {r.status || "—"}
                   </div>
                 </div>

                 {r.remarks && <div className="rc-online-remarks">{r.remarks}</div>}

                 <div className="rc-online-edit-grid">
                   <div>
                     <label>Edit Time</label>
                     <input
                        type="time"
                        value={edits[r.visit_id]?.appointment_time || ""}
                        onChange={(e) =>
                          setEdits((prev) => ({
                            ...prev,
                            [r.visit_id]: { ...(prev[r.visit_id] || {}), appointment_time: e.target.value },
                          }))
                        }
                        disabled={loading}
                        className="rc-online-edit-input"
                      />
                   </div>
                   <div>
                     <label>Edit Doctor</label>
                     <select
                        value={String(edits[r.visit_id]?.assigned_doctor_id ?? "")}
                        onChange={(e) =>
                          setEdits((prev) => ({
                            ...prev,
                            [r.visit_id]: { ...(prev[r.visit_id] || {}), assigned_doctor_id: e.target.value ? Number(e.target.value) : null },
                          }))
                        }
                        disabled={loading || doctors.length === 0}
                        className="rc-online-edit-input"
                      >
                        <option value="">Unassigned</option>
                        {doctors.map((d) => (
                          <option key={d.id} value={d.id}>
                            {d.full_name || d.username || `#${d.id}`}
                          </option>
                        ))}
                     </select>
                   </div>
                 </div>
                 
                 <div className="rc-online-actions">
                   <Button variant="outline" onClick={() => {
                        const edit = edits[r.visit_id] || {};
                        void act(r.visit_id, "confirm", {
                          appointment_time: edit.appointment_time || null,
                          assigned_doctor_id: edit.assigned_doctor_id ?? null,
                        });
                      }} isLoading={loading}>
                      Confirm
                   </Button>
                   <Button variant="primary" onClick={() => void act(r.visit_id, "check-in")} isLoading={loading}>Check-in</Button>
                   <Button variant="secondary" onClick={() => void handleReschedule(r.visit_id)} isLoading={loading}>Reschedule</Button>
                   <Button variant="danger" onClick={() => void act(r.visit_id, "cancel")} isLoading={loading}>Cancel</Button>
                 </div>
              </div>
            ))
          )}
        </div>
      </aside>
    </div>
  );
}
